export const OPERATIONAL_PROJECTION_TONES = Object.freeze({
  created: 'positive',
  reused: 'info',
  skipped: 'warning',
  failed: 'danger',
});

const DESTINATIONS = Object.freeze({
  CrmTask: {
    routeName: 'lais_leads_index',
    query: { view: 'tarefas' },
    action: 'tasks',
  },
  PropertyDemand: {
    routeName: 'lais_property_demands_console',
    action: 'demands',
  },
});

const validTargetId = value => Number.isInteger(value) && value > 0;

export const isKycRequestProjection = projection =>
  projection?.flow === 'kyc_request';

const isKycDocumentRequestProjection = projection =>
  isKycRequestProjection(projection) && projection?.target_type === 'CrmTask';

const kycConsentStatusLabel = (value, t) => {
  if (value === true) {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.CONSENT_STATUSES.ACCEPTED'
    );
  }
  if (value === false) {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.CONSENT_STATUSES.NOT_ACCEPTED'
    );
  }

  return null;
};

const kycDocumentStatusLabel = (value, t) => {
  if (typeof value !== 'string') return null;

  if (value.toLowerCase() === 'identity') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.DOCUMENT_STATUSES.IDENTITY'
    );
  }
  if (value.toLowerCase() === 'documents_pending') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.DOCUMENT_STATUSES.DOCUMENTS_PENDING'
    );
  }
  if (value.toLowerCase() === 'needs_guidance') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.DOCUMENT_STATUSES.NEEDS_GUIDANCE'
    );
  }

  return null;
};

const identityVerificationStatusLabel = (value, t) => {
  if (typeof value !== 'string') return null;

  if (value.toLowerCase() === 'pending') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.PENDING'
    );
  }
  if (value.toLowerCase() === 'provider_pending') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.PROVIDER_PENDING'
    );
  }
  if (value.toLowerCase() === 'approved') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.APPROVED'
    );
  }
  if (value.toLowerCase() === 'rejected') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.REJECTED'
    );
  }
  if (value.toLowerCase() === 'manual_review') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.MANUAL_REVIEW'
    );
  }
  if (value.toLowerCase() === 'expired') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.EXPIRED'
    );
  }
  if (value.toLowerCase() === 'error') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUSES.ERROR'
    );
  }

  return null;
};

export const operationalProjectionKycSummaryRows = (projection, t) => {
  if (!isKycDocumentRequestProjection(projection)) return [];

  const consentStatus = kycConsentStatusLabel(
    projection.kyc_consent_accepted,
    t
  );
  const documentStatus = kycDocumentStatusLabel(
    projection.kyc_document_status,
    t
  );
  const identityVerificationStatus = identityVerificationStatusLabel(
    projection.identity_verification_status,
    t
  );

  return [
    consentStatus && {
      label: t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.CONSENT'),
      value: consentStatus,
    },
    documentStatus && {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.DOCUMENT_STATUS'
      ),
      value: documentStatus,
    },
    identityVerificationStatus && {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.IDENTITY_VERIFICATION_STATUS'
      ),
      value: identityVerificationStatus,
    },
  ].filter(Boolean);
};

export const operationalProjectionStatusLabel = (status, t) => {
  if (status === 'created') {
    return t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.STATUS.CREATED');
  }
  if (status === 'reused') {
    return t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.STATUS.REUSED');
  }
  if (status === 'skipped') {
    return t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.STATUS.SKIPPED');
  }

  return t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.STATUS.FAILED');
};

export const operationalProjectionTone = status =>
  OPERATIONAL_PROJECTION_TONES[status] || OPERATIONAL_PROJECTION_TONES.failed;

export const operationalProjectionTargetLabel = (
  projection,
  t,
  { includeId = false } = {}
) => {
  if (isKycRequestProjection(projection)) {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.KYC_DOCUMENT_REQUEST'
    );
  }

  const label = {
    PublicVisitRequest: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.VISIT_REQUEST'
    ),
    PropertyOffer: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.PROPERTY_OFFER'
    ),
    PropertyDemand: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.PROPERTY_DEMAND'
    ),
    PropertyMaintenanceCase: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.MAINTENANCE_CASE'
    ),
    LeaseInspection: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.LEASE_INSPECTION'
    ),
    SupportTicket: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.SUPPORT_TICKET'
    ),
    Imovel: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.PROPERTY'
    ),
    CrmTask: t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TARGET.CRM_TASK'
    ),
  }[projection?.target_type];
  if (!label) {
    return t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.NOT_CREATED');
  }

  return includeId && validTargetId(projection.target_id)
    ? `${label} #${projection.target_id}`
    : label;
};

export const operationalProjectionSummaryLabel = (projection, t) => {
  const target = operationalProjectionTargetLabel(projection, t);
  const status = operationalProjectionStatusLabel(projection?.status, t);
  return `${target} · ${status}`;
};

export const operationalProjectionDestination = (projection, accountId) => {
  if (isKycRequestProjection(projection)) return null;

  const destination = DESTINATIONS[projection?.target_type];
  if (!destination || !validTargetId(projection?.target_id) || !accountId) {
    return null;
  }

  return {
    action: destination.action,
    route: {
      name: destination.routeName,
      params: { accountId },
      query: destination.query,
    },
  };
};
